// =============================================================================
// timetable-clusters.ts – Grouping trips into collapsible clusters
// =============================================================================

import type { Trip, TimeCluster, ClusterConfig } from "./types.js";
import { getReferenceTime } from "./timetable-data.js";
import { minsToHHMM } from "./utils.js";

const CLUSTER_IDS = ["early_morning", "commuter_am", "midday", "commuter_pm", "evening", "night"];

export const DEFAULT_CLUSTER_CONFIG: ClusterConfig = {
  boundaries: [0, 420, 540, 780, 960, 1200, 1440],
  labels: [
    "Primo mattino",
    "Pendolari mattina",
    "Mattina/Ora attuale",
    "Pomeriggio",
    "Pendolari sera",
    "Sera"
  ]
};

/**
 * Groups trips into clusters based on their reference time.
 * The active cluster (the one containing the next trip after currentMin) will be set as open.
 */
export function clusterTrips(
  trips: Trip[],
  currentMin: number,
  referenceStops: string[],
  config: ClusterConfig = DEFAULT_CLUSTER_CONFIG
): TimeCluster[] {
  // Initialize clusters from config
  const clusters: TimeCluster[] = config.labels.map((label, i) => ({
    id: CLUSTER_IDS[i] || `cluster_${i}`,
    label,
    range: "",
    trips: [],
    isActive: false,
    collapsed: true,
    startMin: config.boundaries[i],
    endMin: config.boundaries[i + 1]
  }));

  // Assign each trip to a cluster based on reference time
  for (const trip of trips) {
    const refMin =
      trip._refMin !== undefined ? trip._refMin : getReferenceTime(trip, referenceStops);
    if (refMin === null || refMin === undefined) continue;

    const idx = config.boundaries.findIndex(
      (b, i) => i < config.boundaries.length - 1 && refMin >= b && refMin < config.boundaries[i + 1]
    );
    if (idx >= 0) {
      clusters[idx].trips.push(trip);
    }
  }

  // Find the next trip overall to mark the active cluster
  let nextTrip: Trip | null = null;
  let minDiff = Number.POSITIVE_INFINITY;
  for (const trip of trips) {
    const refMin =
      trip._refMin !== undefined ? trip._refMin : getReferenceTime(trip, referenceStops);
    if (refMin !== null && refMin !== undefined && refMin >= currentMin) {
      const diff = refMin - currentMin;
      if (diff < minDiff) {
        minDiff = diff;
        nextTrip = trip;
      }
    }
  }

  let activeIdx = -1;
  if (nextTrip) {
    activeIdx = clusters.findIndex((c) => c.trips.includes(nextTrip!));
  }

  if (activeIdx >= 0) {
    clusters[activeIdx].isActive = true;
    clusters[activeIdx].collapsed = false;
  }

  // Calculate range label and remove empty clusters
  return clusters
    .filter((c) => c.trips.length > 0)
    .map((c) => {
      const times = c.trips
        .map((t) => (t._refMin !== undefined ? t._refMin : getReferenceTime(t, referenceStops)))
        .filter((t): t is number => t !== null && t !== undefined);
      if (times.length > 0) {
        c.range = `${minsToHHMM(Math.min(...times))} – ${minsToHHMM(Math.max(...times))}`;
      } else {
        c.range = "";
      }
      return c;
    });
}

/**
 * Returns the index of the active cluster (contains the next trip).
 */
export function getActiveClusterIndex(clusters: TimeCluster[]): number {
  return clusters.findIndex((c) => c.isActive);
}
